import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Backdrop,
  Fade,
  Stack,
  TextField,
  MenuItem,
  Divider,
  Grid,
  Chip,
  Paper,
  Autocomplete,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton
} from "@mui/material";
import * as XLSX from "xlsx";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useUploadBulkEmployeeMaster } from "../../hooks/useEmployees";
import { exportEmployeeMasterData, GET_BRANCHES, GET_TEAM_HEADS, GET_LITE_EMPLOYEE_DETAILS } from "../../api";
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from "../common/tableStyles";
import './excell.css'

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

// Soft white action tile: colored icon tile + bold title + grey subtitle.
const ActionTile = ({ icon, color, title, subtitle, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      ...CARD_SX,
      textAlign: 'center',
      cursor: 'pointer',
      height: '100%',
      transition: 'all 0.2s ease',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(17,24,39,0.08)' },
    }}
  >
    <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
      {React.cloneElement(icon, { sx: { fontSize: 28 } })}
    </Box>
    <Typography sx={{ fontWeight: 800, color: '#111827' }}>{title}</Typography>
    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>{subtitle}</Typography>
  </Paper>
);

const MANDATORY_HEADERS = [
  "Employee ID", "Employee Name", "DOJ", "Gender", "DOB", "Email ID",
  "Status", "Confirmation Date", "Manager Name", "Manager ID", "Phone No",
  "Employment Status", "PAN No", "UAN No", "IFSC Code", "Bank A/c No", "Nationality",
  "Location", "Designation", "Department", "Grade", "Attendancescheme",
  "Costcenter", "BranchName", "Division", "Adhhar No", "Team Head", "ESI No"
];

const Empxcel = () => {
  const [openUpload, setOpenUpload] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [file, setFile] = useState(null);
  const [showReference, setShowReference] = useState(false);

  // Filter Options Data
  const [branchOptions, setBranchOptions] = useState([]);
  const [teamHeadOptions, setTeamHeadOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const [filters, setFilters] = useState({
    record_status: "all",
    branch: "",
    team_head: "",
    employee_no: "",
    search: ""
  });

  const { mutate: uploadMaster, isPending: isUploading } = useUploadBulkEmployeeMaster();

  // Fetch filter options on mount or modal open
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const [branches, teamHeads, emps] = await Promise.all([
          GET_BRANCHES(),
          GET_TEAM_HEADS(),
          GET_LITE_EMPLOYEE_DETAILS()
        ]);

        setBranchOptions(branches || []);
        setTeamHeadOptions(teamHeads || []);
        setEmployeeOptions(emps || []);
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    if (openExport) {
      fetchOptions();
    }
  }, [openExport]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = () => {
    if (file) {
      setUploadResults(null);
      uploadMaster(file, {
        onSuccess: (data) => {
          const results = data.results;
          setUploadResults(results);
          if (results.failed > 0 && results.failed_data?.length > 0) {
            downloadFailedRecords(results.failed_data);
          }
          if (results.failed === 0) {
            setFile(null);
          }
        }
      });
    }
  };

  const downloadFailedRecords = (failedData = null) => {
    // Check if failedData is the correct array; if it's an event (from manual button), use fallback
    const dataToExport = Array.isArray(failedData) ? failedData : uploadResults?.failed_data;

    if (!dataToExport || !Array.isArray(dataToExport) || dataToExport.length === 0) {
      console.warn("No failed records found to download.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Records");
    XLSX.writeFile(workbook, `failed_upload_correction_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExport = (template = false) => {
    exportEmployeeMasterData({ ...filters, template });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CloudUploadIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Bulk Employee Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Download a template, export current data, or upload an Excel file to create and update employee records.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Step 1: Reference */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={CARD_SX}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none'
              }}
              onClick={() => setShowReference(!showReference)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <HelpOutlineIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight="800" sx={{ color: '#111827' }}>
                  Excel Header Reference
                </Typography>
              </Box>
              <IconButton size="small">
                {showReference ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>

            <Collapse in={showReference}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef0f4' }}>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                  Please ensure your Excel file contains these exact headers. <strong>Employee ID</strong> and <strong>Employee Name</strong> are mandatory.
                  Blank cells will be ignored (partial update).
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {MANDATORY_HEADERS.map((h) => (
                    <Chip
                      key={h}
                      label={h}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        borderRadius: '8px',
                        bgcolor: '#f3f4f6',
                        color: '#475569',
                        border: 'none',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        {/* Action Cards */}
        <Grid item xs={12} md={4}>
          <ActionTile
            icon={<DownloadIcon />}
            color={ACCENT}
            title="Download Template"
            subtitle="Empty Excel with all correct headers."
            onClick={() => handleExport(true)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ActionTile
            icon={<AssessmentIcon />}
            color={ACCENT}
            title="Export Current Data"
            subtitle="Download existing data with filters for modification."
            onClick={() => setOpenExport(true)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ActionTile
            icon={<UploadFileIcon />}
            color={ACCENT}
            title="Upload Master Data"
            subtitle="Upload file to create or update employee records."
            onClick={() => setOpenUpload(true)}
          />
        </Grid>
      </Grid>

      {/* Upload Modal */}
      <Modal open={openUpload} onClose={() => { setOpenUpload(false); setUploadResults(null); }} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
        <Fade in={openUpload}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: uploadResults ? 800 : 450,
            maxHeight: '90vh', overflowY: 'auto',
            ...CARD_SX, p: 4
          }}>
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, fontWeight: 800, color: '#111827' }}>Upload Employee Master</Typography>

            {!uploadResults ? (
              <Stack spacing={3}>
                <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />} sx={OUTLINED_BTN}>
                  {file ? file.name : "Select Excel File"}
                  <input type="file" hidden accept=".xlsx, .xls" onChange={handleFileChange} />
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  fullWidth
                  onClick={handleUpload}
                  disabled={!file || isUploading}
                  startIcon={<UploadFileIcon />}
                  sx={GRADIENT_BTN}
                >
                  {isUploading ? "Uploading..." : "Start Upload"}
                </Button>
              </Stack>
            ) : (
              <Box>
                <Alert severity={uploadResults.failed > 0 ? "warning" : "success"} sx={{ mb: 3, borderRadius: '14px' }}>
                  <AlertTitle>{uploadResults.failed > 0 ? "Upload Completed with Issues" : "Upload Successful"}</AlertTitle>
                  Processed {uploadResults.successful + uploadResults.failed} records.
                  <strong> {uploadResults.successful} Successful</strong>,
                  <strong> {uploadResults.failed} Failed</strong>.
                </Alert>

                {uploadResults.failed > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#ef4444' }}>Error Details</Typography>
                      <Button
                        variant="contained"
                        disableElevation
                        size="small"
                        color="error"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadFailedRecords()}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}
                      >
                        Download Failed Records
                      </Button>
                    </Box>
                    <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', maxHeight: 350 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ ...HEAD_CELL_SX, width: '60px' }}>Row</TableCell>
                            <TableCell sx={{ ...HEAD_CELL_SX, width: '120px' }}>Emp ID</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Error Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadResults.errors.map((err, i) => (
                            <TableRow key={i} sx={TABLE_ROW_SX}>
                              <TableCell sx={BODY_CELL_SX}>{err.row}</TableCell>
                              <TableCell sx={{ ...BODY_CELL_SX, fontWeight: 600 }}>{err.emp_id}</TableCell>
                              <TableCell sx={{ ...BODY_CELL_SX, color: '#ef4444' }}>{err.error}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Button
                  fullWidth variant="outlined" sx={{ ...OUTLINED_BTN, mt: 3 }}
                  onClick={() => { setOpenUpload(false); setUploadResults(null); }}
                >
                  Close
                </Button>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Export/Filter Modal */}
      <Modal open={openExport} onClose={() => setOpenExport(false)} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
        <Fade in={openExport}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 550, ...CARD_SX, p: 4 }}>
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, fontWeight: 800, color: '#111827' }}>Export Employee Data</Typography>

            {loadingOptions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth label="Employee Status" size="small"
                    value={filters.record_status}
                    onChange={(e) => setFilters({ ...filters, record_status: e.target.value })}
                  >
                    <MenuItem value="all">All Employees (Active + Inactive)</MenuItem>
                    <MenuItem value="active">Active Employees</MenuItem>
                    <MenuItem value="inactive">Inactive Employees</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={employeeOptions}
                    getOptionLabel={(option) => `${option.name} (${option.employee_no})`}
                    onChange={(event, newValue) => {
                      setFilters({
                        ...filters,
                        search: newValue ? newValue.name : "",
                        employee_no: newValue ? newValue.employee_no : ""
                      });
                    }}
                    renderInput={(params) => <TextField {...params} label="Select Employee (Name or ID)" size="small" />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth label="Branch" size="small"
                    value={filters.branch} onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
                  >
                    <MenuItem value=""><em>All Branches</em></MenuItem>
                    {branchOptions.map((b) => (
                      <MenuItem key={b.id} value={b.name}>{b.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth label="Team Head" size="small"
                    value={filters.team_head} onChange={(e) => setFilters({ ...filters, team_head: e.target.value })}
                  >
                    <MenuItem value=""><em>All Team Heads</em></MenuItem>
                    {teamHeadOptions.map((t) => (
                      <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Select any filter to download specific data, or leave blank to download all.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained" disableElevation fullWidth onClick={() => { handleExport(); setOpenExport(false); }}
                    startIcon={<DownloadIcon />}
                    sx={{ ...GRADIENT_BTN, mt: 1 }}
                  >
                    Download Filtered Excel
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Empxcel;
